import { TrackerAlgorithm } from './trackerMath';

// Developer-facing tracker experiment settings, switchable live from the debug overlay
// (?aiDebug=1). Session-only on purpose: localStorage persistence was removed because testers
// cannot clear it on phones, and stored settings masked new defaults. Not customer-facing
// configuration — once a winner is clear these become fixed constants again.
// Visual guidance effects, individually toggleable so variants can be compared (and combined)
// live on the device: 'particles' = stream from meter box to target frame, 'blur' = everything
// outside the meter box is blurred/dimmed (focus look), 'metaballs' = gooey blob strands
// connecting the meter-box corners with the target-frame corners (metaball effect),
// 'cornerLines' = thin corner-to-corner lines with particles flowing along them,
// 'scrim' = dim everything outside the target frame (bright scanner window).
export type AlignmentEffect = 'particles' | 'blur' | 'metaballs' | 'cornerLines' | 'scrim';

// How the SOLL frame brackets are colored: 'neutral' keeps them quiet white until the snap
// (color stays the IST feedback channel), 'ramp' follows the score ramp (legacy behavior),
// 'cyan' uses a fixed contrast color distinct from the ramp.
export type FrameColorMode = 'neutral' | 'ramp' | 'cyan';
export type FrameLineStyle = 'solid' | 'dashed';

// Which endpoint feeds the live box-tracking loop: 'full' = evaluateImage delivers boxes and
// values (meter number, rates) in one call — today's behavior. 'fast' = detect/fast delivers
// only boxes, polled more often/cheaper; the meter number and readable values then come exclusively
// from the hi-res crop reads (which also decode DataMatrix server-side) once the tracker is
// close/aligned.
export type DetectionSource = 'full' | 'fast';

export interface TrackerSettings {
    algorithm: TrackerAlgorithm;
    keypointCount: number;
    effects: AlignmentEffect[];
    frameColorMode: FrameColorMode;
    frameLineStyle: FrameLineStyle;
    detectionSource: DetectionSource;
}

export const TRACKER_ALGORITHMS: TrackerAlgorithm[] = ['sad', 'ncc', 'klt'];
export const TRACKER_KEYPOINT_COUNTS = [12, 24, 40];
export const ALIGNMENT_EFFECTS: AlignmentEffect[] = ['particles', 'blur', 'metaballs', 'cornerLines', 'scrim'];
export const FRAME_COLOR_MODES: FrameColorMode[] = ['neutral', 'ramp', 'cyan'];
export const FRAME_LINE_STYLES: FrameLineStyle[] = ['solid', 'dashed'];
export const DETECTION_SOURCES: DetectionSource[] = ['full', 'fast'];

// NCC won the on-device comparison (2026-07): its illumination-invariant matching keeps far more
// keypoints alive while panning (mobile auto-exposure shifts brightness), which keeps the
// consensus stable — visibly the smoothest box motion of the three matchers.
// Default effects chosen in the 2026-07-07 visuals review: corner lines + scrim replace the
// diffuse particle stream; the older effects stay available as toggles.
// Fast detection promoted to default (2026-07-08 device comparison): the detection-only
// endpoint anchors the tracker twice as often; number/value reading runs via the gated
// hi-res crops either way. 'full' stays available as a toggle.
export const DEFAULT_TRACKER_SETTINGS: TrackerSettings = {
    algorithm: 'ncc',
    keypointCount: 24,
    effects: ['cornerLines', 'scrim'],
    frameColorMode: 'neutral',
    frameLineStyle: 'solid',
    detectionSource: 'fast',
};
